<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="store.loading" />

  <div v-else class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">طرق الدفع</h1>
          <p class="ds-page-header__subtitle">إدارة وسائل الدفع المتاحة للطلاب عند الاشتراك</p>
        </div>
        <div class="ds-page-header__actions">
          <router-link to="/payment-methods/create" class="ds-btn ds-btn--primary">
            <i class="pi pi-plus"></i> إضافة طريقة دفع
          </router-link>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!store.methods.length" class="empty-state empty-state--card">
        <div class="empty-state__icon"><i class="pi pi-wallet"></i></div>
        <h3 class="empty-state__title">لا توجد طرق دفع</h3>
        <p class="empty-state__desc">أضف طريقة الدفع الأولى ليتمكن الطلاب من الاشتراك.</p>
        <router-link to="/payment-methods/create" class="ds-btn ds-btn--primary empty-state__action">
          <i class="pi pi-plus"></i> إضافة طريقة دفع
        </router-link>
      </div>

      <template v-else>
        <!-- Desktop / tablet table -->
        <div class="ds-table-wrap table-responsive">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">الشعار</th>
                <th>الاسم</th>
                <th>اسم الحساب</th>
                <th>رقم المحفظة / الحساب</th>
                <th class="text-center">الحالة</th>
                <th class="text-center pe-3">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="m in store.methods" :key="m.id">
                <td class="ps-3">
                  <img v-if="m.image_link" :src="m.image_link" class="pm-thumb" alt="" />
                  <span v-else class="pm-thumb pm-thumb--empty"><i class="pi pi-wallet"></i></span>
                </td>
                <td class="fw-bold">{{ m.name }}</td>
                <td>{{ m.account_name || '—' }}</td>
                <td class="pm-number">{{ m.account_number || m.iban || '—' }}</td>
                <td class="text-center">
                  <button
                    class="chip-toggle" :class="m.is_active ? 'chip-toggle--on' : 'chip-toggle--off'"
                    v-tooltip.top="m.is_active ? 'اضغط للتعطيل' : 'اضغط للتفعيل'"
                    @click="toggle(m)"
                  >
                    {{ m.is_active ? 'مفعّل' : 'معطّل' }}
                  </button>
                </td>
                <td class="text-center pe-3">
                  <div class="icon-action-group justify-content-center">
                    <router-link :to="`/payment-methods/${m.id}/edit`" class="icon-action-btn icon-action-btn--edit" v-tooltip.top="'تعديل'">
                      <i class="pi pi-pencil"></i>
                    </router-link>
                    <button class="icon-action-btn icon-action-btn--danger" v-tooltip.top="'حذف'" @click="remove(m)">
                      <i class="pi pi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile cards -->
        <div class="mobile-list">
          <div v-for="m in store.methods" :key="m.id" class="mobile-list-card">
            <div class="mobile-list-card__top">
              <img v-if="m.image_link" :src="m.image_link" class="mobile-list-card__thumb" alt="" />
              <span v-else class="mobile-list-card__thumb"><i class="pi pi-wallet"></i></span>
              <div class="mobile-list-card__meta">
                <div class="mobile-list-card__title">{{ m.name }}</div>
                <small class="text-muted">{{ m.account_name || '' }}</small>
              </div>
              <button class="chip-toggle" :class="m.is_active ? 'chip-toggle--on' : 'chip-toggle--off'" @click="toggle(m)">
                {{ m.is_active ? 'مفعّل' : 'معطّل' }}
              </button>
            </div>
            <div v-if="m.account_number || m.iban" class="mobile-list-card__facts">
              <span><i class="pi pi-hashtag"></i> {{ m.account_number || m.iban }}</span>
            </div>
            <div class="mobile-list-card__actions">
              <router-link :to="`/payment-methods/${m.id}/edit`" class="ds-btn ds-btn--secondary ds-btn--sm">
                <i class="pi pi-pencil"></i> تعديل
              </router-link>
              <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="remove(m)">
                <i class="pi pi-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import { usePaymentMethodStore } from '@/stores/usePaymentMethodStore.js';
import { apiError } from '@/composables/subscriptionMeta.js';

export default {
  name: 'PaymentMethodsIndex',
  components: { Breadcrumb, Loading },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'طرق الدفع', path: '/payment-methods' }],
    };
  },
  computed: {
    store() { return usePaymentMethodStore(); },
  },
  methods: {
    async toggle(m) {
      try { await this.store.toggle(m.id); }
      catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false }); }
    },
    async remove(m) {
      const res = await Swal.fire({
        title: 'حذف طريقة الدفع', html: `سيتم حذف <strong>${m.name}</strong> نهائياً.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.deleteMethod(m.id);
        Swal.fire({ icon: 'success', title: 'تم الحذف', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
  },
  mounted() {
    this.store.fetchMethods();
  },
};
</script>

<style scoped>
.pm-thumb {
  width: 42px; height: 42px; border-radius: var(--radius-md); object-fit: cover; display: inline-flex;
  align-items: center; justify-content: center; background: var(--primary-light); color: var(--primary-color);
  border: 1px solid var(--surface-border); font-size: 1.0625rem; flex-shrink: 0;
}
.pm-number { font-family: 'Courier New', monospace; color: var(--text-secondary); letter-spacing: 0.4px; }
</style>
